import { createFileRoute } from '@tanstack/react-router';
import { SearchEmbed as SearchEmbedType } from '@thoughtspot/visual-embed-sdk';
import { SearchEmbed } from '@thoughtspot/visual-embed-sdk/react';
import { useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { RefObject } from 'react';
import { searchConfig } from '@/customizations/thoughtspotCustomizations';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';

/**
 * DEEP LINK ROUTE: Search with Data Source
 *
 * This route serves as a direct access point for sharing/bookmarking ThoughtSpot searches
 * with a pre-selected data source.
 * Users typically land here via:
 * - Shared search links with specific data source
 * - Email links containing search URLs
 * - Bookmarks
 * - External integrations
 *
 * URL Pattern: /dashboard/search/{guid}
 * Example: /dashboard/search/cd252e5c-b552-49a8-821d-3eadaa049cca
 *
 * This route uses a direct SearchEmbed component with its own independent iframe,
 * not the prerendered shared iframe from PrerenderedThoughtSpotProvider.
 *
 * Note: This is distinct from /dashboard/search (primary search interface without data source).
 */
export const Route = createFileRoute('/dashboard/_deep-links/search/$guid')({
  component: RouteComponent,
});

function RouteComponent() {
  const ref = useEmbedRef() as RefObject<SearchEmbedType>;
  const userAppViewConfig = useUserAppViewConfig();
  const { guid } = Route.useParams();

  return (
    <SearchEmbed
      ref={ref}
      dataSource={guid}
      className="h-[calc(100vh-var(--dashboard-menu-height))] w-full"
      {...searchConfig}
      {...(userAppViewConfig?.hiddenActions && {
        hiddenActions: userAppViewConfig.hiddenActions,
      })}
    />
  );
}
